import React, { useState, useEffect } from 'react';

import { Box, Button, Chip, CircularProgress, Grid, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { Tabla } from 'ui-component';
import AutocompleteForm from 'ui-component/AutocompleteForm';
import MainCard from 'ui-component/cards/MainCard';
import { useLogin } from 'Context/ContextLogin';
import Services from './Components/services';

// Filtros por columna, paginación y exportación a Excel los aporta el propio
// componente Tabla. Los labels van en minúscula porque Tabla los pinta con
// textTransform uppercase.
const columnsNotificacion = [
    { label: 'número de empleado', name: 'num_empleado', filtrar: true },
    { label: 'nombre completo', name: 'nombre_persona', filtrar: true },
    { label: 'tipo', name: 'tipo_puesto', filtrar: true },
    { label: 'puesto', name: 'nom_puesto', filtrar: true },
    { label: 'departamento', name: 'nom_area', filtrar: true },
    { label: 'área', name: 'nom_subarea', filtrar: true },
    { label: 'correo institucional', name: 'email_empresarial', filtrar: true },
    { label: 'tipo de correo', name: 'nombreTipoCorreo', filtrar: true },
    { label: 'correo enviado', name: 'nEstadoEnvio', filtrar: true },
    { label: 'fecha de envío', name: 'fechaEnvio', filtrar: true }
];

// idarea (tbl_area, el mismo catálogo que usa el reporte Seguimiento) -> permiso
// que habilita la pestaña. El backend traduce este idarea a tipo_puesto de RH,
// que es donde vive la clasificación real (verificado contra datos reales).
const PERMISO_POR_AREA = {
    1: 'verAdmin',
    2: 'verOpe',
    3: 'verVen',
    4: 'verOoam'
};

// Pseudo-opciones agregadas solo en frontend: no viven en los catálogos.
const OPCION_TODOS = { idSeguimientoNotificacion: 'TODOS', nombre: 'Todos', valorEstadoEnvio: null };
const TIPO_TODOS = { idTipoCorreo: 'TODOS', nombre: 'Todos' };

// Chip de un filtro aplicado: etiqueta en semibold y valor en peso normal.
const ChipFiltro = ({ etiqueta, valor }) => (
    <Chip
        size="small"
        variant="outlined"
        color="primary"
        label={
            <>
                <Box component="span" sx={{ fontWeight: 600 }}>
                    {etiqueta}:
                </Box>{' '}
                {valor}
            </>
        }
    />
);

// Texto del chip de total. El backend no pagina, así que registros.length es el
// total real de la búsqueda, no el de la página visible.
const textoTotal = (total) => {
    if (total === 0) return 'Sin registros';
    if (total === 1) return '1 registro encontrado';
    return `${total.toLocaleString('es-MX')} registros encontrados`;
};

// Resumen de la ÚLTIMA búsqueda aplicada. Se pinta dentro de la barra de
// herramientas de Tabla (prop toolExtra), en el hueco a la izquierda de los
// botones de Excel y búsqueda.
const ResumenBusqueda = ({ area, tipoCorreo, seguimiento, total, cargando }) => (
    <Box sx={{ py: 1 }}>
        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'grey.500', letterSpacing: '0.5px' }}>
            ÚLTIMA BÚSQUEDA
        </Typography>
        {/* gap en vez de spacing: el spacing de Stack es por margen y se
            desalinea cuando los chips saltan de renglón. */}
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
            <ChipFiltro etiqueta="Área" valor={area || '—'} />
            <ChipFiltro etiqueta="Tipo de correo" valor={tipoCorreo} />
            <ChipFiltro etiqueta="Seguimiento" valor={seguimiento} />
            <Chip size="small" color="primary" label={cargando || total == null ? 'Buscando…' : textoTotal(total)} />
        </Stack>
    </Box>
);

const SeguimientoNotificacion = ({ permisos }) => {
    const services = Services();
    const { control, setValue } = useForm();
    const {
        usuario: { areas }
    } = useLogin();

    const [areasActual, setAreasActual] = useState([]);
    const [catalogoEstado, setCatalogoEstado] = useState([OPCION_TODOS]);
    const [catalogoTipo, setCatalogoTipo] = useState([TIPO_TODOS]);

    // Filtros SELECCIONADOS: lo que el usuario va eligiendo. Cambiarlos no
    // consulta nada; solo se aplican al presionar Buscar.
    const [areaSeleccionada, setAreaSeleccionada] = useState(null);
    const [tipoSeleccionado, setTipoSeleccionado] = useState(TIPO_TODOS);
    const [estadoSeleccionado, setEstadoSeleccionado] = useState(OPCION_TODOS);

    const [registros, setRegistros] = useState([]);
    const [cargandoTabla, setCargandoTabla] = useState(false);

    // Se incrementa en cada búsqueda aplicada y sirve de key de la Tabla: la
    // remonta y con ello reinicia la paginación a la primera página, incluso si
    // se vuelve a buscar con los mismos filtros.
    const [numeroBusqueda, setNumeroBusqueda] = useState(0);

    // Filtros REALMENTE aplicados en la última consulta, no los que el usuario
    // tiene seleccionados sin haber presionado Buscar. Se guarda idArea y no el
    // nombre porque en la carga inicial areasActual todavía no está en estado;
    // el nombre se resuelve al renderizar.
    const [ultimaBusqueda, setUltimaBusqueda] = useState(null);

    // Único punto que llama al servicio del reporte. Recibe los tres filtros
    // explícitamente para no depender de estados aún no aplicados por React.
    const consultar = (area, tipo, estado) => {
        if (area == null) return;

        // "Todos" es una pseudo-opción del frontend: al backend se le manda null,
        // que es como pide "sin filtro".
        const idTipo = !tipo || tipo.idTipoCorreo === TIPO_TODOS.idTipoCorreo ? null : tipo.idTipoCorreo;

        setCargandoTabla(true);
        setNumeroBusqueda((n) => n + 1);

        // El resumen se arma con los filtros de ESTA consulta; el total llega
        // hasta que responde el servicio. Reemplaza al resumen anterior.
        setUltimaBusqueda({
            idArea: area,
            tipoCorreo: (tipo || TIPO_TODOS).nombre,
            seguimiento: (estado || OPCION_TODOS).nombre,
            total: null
        });

        services.getReporteNotificacion(
            (ct) => {
                const encontrados = ct.registros || [];
                setRegistros(encontrados);
                setUltimaBusqueda((previa) => ({ ...previa, total: encontrados.length }));
                setCargandoTabla(false);
            },
            {
                area,
                estadoEnvio: estado?.valorEstadoEnvio ?? null,
                idTipoCorreo: idTipo
            },
            // El loading se pinta sobre la tabla, no sobre toda la pantalla.
            { backdrop: false }
        );
    };

    // Los handlers de filtro solo guardan la selección: ninguno consulta.
    const clickCambioTipo = (event, value) => setAreaSeleccionada(value);

    const cambiaTipoCorreo = (item) => {
        // El Autocomplete permite limpiar el campo; se repone "Todos" tanto en
        // el estado como en el valor visible del input.
        const seleccion = item || TIPO_TODOS;
        if (!item) setValue('tipoCorreo', TIPO_TODOS);
        setTipoSeleccionado(seleccion);
    };

    const cambiaEstado = (item) => {
        const seleccion = item || OPCION_TODOS;
        if (!item) setValue('estadoSeguimiento', OPCION_TODOS);
        setEstadoSeleccionado(seleccion);
    };

    const clickBuscar = () => consultar(areaSeleccionada, tipoSeleccionado, estadoSeleccionado);

    // Los catálogos se cargan una sola vez; Buscar no vuelve a pedirlos.
    useEffect(() => {
        services.getCatalogoSeguimientoNotificacion((ct) => {
            setCatalogoEstado([OPCION_TODOS, ...(ct.catalogo || [])]);
        });
        services.getCatalogoTipoCorreo((ct) => {
            setCatalogoTipo([TIPO_TODOS, ...(ct.catalogo || [])]);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!areas?.length) return;

        // Orden fijo Administrativo -> Operativo -> Ventas -> OOAM, igual que
        // Seguimiento.js: get_areasByusuario no lleva ORDER BY.
        const permitidas = areas
            .filter((element) => permisos[PERMISO_POR_AREA[Number(element.idarea)]] == true)
            .sort((a, b) => Number(a.idarea) - Number(b.idarea));

        setAreasActual(permitidas);

        // Única consulta automática: la carga inicial, para no abrir en blanco.
        if (permitidas.length > 0) {
            setAreaSeleccionada(permitidas[0].idarea);
            consultar(permitidas[0].idarea, TIPO_TODOS, OPCION_TODOS);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [areas]);

    return (
        <MainCard title="Seguimiento de notificación ">
            <Grid container spacing={2} alignItems="center">
                {areasActual.length > 0 && (
                    <Grid item xs={12} md="auto">
                        <Tabs
                            value={areaSeleccionada ? areaSeleccionada : areasActual[0].idarea}
                            onChange={clickCambioTipo}
                            textColor="inherit"
                            variant="scrollable"
                            scrollButtons="auto"
                            aria-label="clasificación de personal"
                            sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40 } }}
                        >
                            {areasActual.map((area) => (
                                <Tab key={area.idarea} label={area.narea} value={area.idarea} />
                            ))}
                        </Tabs>
                    </Grid>
                )}
                <Grid item xs={12} sm={6} md={3}>
                    <AutocompleteForm
                        id="selectTipoCorreo"
                        label="Tipo de correo"
                        control={control}
                        name="tipoCorreo"
                        rules={{}}
                        defaultValue={TIPO_TODOS}
                        options={catalogoTipo}
                        optionSelected={{ label: 'nombre', id: 'idTipoCorreo' }}
                        onChange={cambiaTipoCorreo}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <AutocompleteForm
                        id="selectEstadoSeguimiento"
                        label="Seguimiento de notificación"
                        control={control}
                        name="estadoSeguimiento"
                        rules={{}}
                        defaultValue={OPCION_TODOS}
                        options={catalogoEstado}
                        optionSelected={{ label: 'nombre', id: 'idSeguimientoNotificacion' }}
                        onChange={cambiaEstado}
                    />
                </Grid>
                <Grid item xs={12} md="auto">
                    <Button type="button" variant="contained" color="primary" onClick={clickBuscar} disabled={cargandoTabla}>
                        Buscar
                    </Button>
                </Grid>
                {areaSeleccionada != null && (
                    <Grid item xs={12}>
                        <Box sx={{ position: 'relative' }}>
                            {cargandoTabla && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        zIndex: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(255,255,255,0.65)'
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            )}
                            <Tabla
                                key={numeroBusqueda}
                                rows={registros}
                                columns={columnsNotificacion}
                                columnKey="num_empleado"
                                toolExtra={
                                    ultimaBusqueda && (
                                        <ResumenBusqueda
                                            // idarea llega como string desde el catálogo, por eso ==.
                                            area={areasActual.find((item) => item.idarea == ultimaBusqueda.idArea)?.narea}
                                            tipoCorreo={ultimaBusqueda.tipoCorreo}
                                            seguimiento={ultimaBusqueda.seguimiento}
                                            total={ultimaBusqueda.total}
                                            cargando={cargandoTabla}
                                        />
                                    )
                                }
                            />
                        </Box>
                    </Grid>
                )}
            </Grid>
        </MainCard>
    );
};

export default SeguimientoNotificacion;
